import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient, apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore, type AuthUser, type UserRole } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { PasswordField } from "@/components/password-field";
import { isPasswordValid } from "@/lib/validation/password";
import { toast } from "@/components/ui/toast";

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Quản trị",
  manager: "Quản lý",
  cashier: "Thu ngân",
  warehouse: "Kho",
  sales: "Bán hàng + Nhập kho",
  technician: "Kỹ thuật",
  viewer: "Chỉ xem",
};

export function AccountPage() {
  const storeUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const { data: freshUser } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => apiGet<AuthUser>("/v1/auth/me"),
  });

  useEffect(() => {
    if (freshUser) setUser(freshUser);
  }, [freshUser, setUser]);

  const user = freshUser ?? storeUser;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const newValid = isPasswordValid(newPassword);
  const confirmMatch = newPassword === confirmPassword;
  const canSubmit =
    currentPassword.length > 0 && newValid && confirmMatch;

  const changePW = useMutation({
    mutationFn: () =>
      apiClient.patch("/v1/auth/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      toast.success("Đổi mật khẩu thành công");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
    },
    onError: (e) => setError(translateApiError(e)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    changePW.mutate();
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="text-2xl font-semibold">Tài khoản</h1>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Thông tin tài khoản</h2>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <InfoRow label="Họ tên" value={user?.fullName} />
          <InfoRow label="Email" value={user?.email} />
          <InfoRow label="Số điện thoại" value={user?.phone || "—"} />
          <div className="flex items-center gap-2">
            <span className="w-32 text-muted-foreground">Vai trò</span>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {ROLE_LABEL[user?.role ?? "cashier"]}
            </span>
          </div>
          <p className="pt-2 text-xs text-muted-foreground">
            Liên hệ quản trị viên để thay đổi thông tin.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">Đổi mật khẩu</h2>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <PasswordField
              id="acc-current"
              label="Mật khẩu hiện tại"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              required
            />
            <PasswordField
              id="acc-new"
              label="Mật khẩu mới"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              required
              showRules
            />
            <PasswordField
              id="acc-confirm"
              label="Xác nhận mật khẩu mới"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
              error={
                confirmPassword && !confirmMatch
                  ? "Mật khẩu xác nhận không khớp"
                  : undefined
              }
            />
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <Button type="submit" disabled={changePW.isPending || !canSubmit}>
              {changePW.isPending ? "Đang đổi…" : "Đổi mật khẩu"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-32 text-muted-foreground">{label}</span>
      <span>{value || "—"}</span>
    </div>
  );
}
